import { faSpinner } from "@jewlr/pro-regular-svg-icons/faSpinner"
import { useInfiniteQuery } from "@tanstack/react-query"
import PropTypes from "prop-types"
import queryString from "query-string"
import { useCallback, useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { Link, useLocation } from "react-router-dom"

import api from "apis"
import { history } from "app-store-v2"
import Button from "components/ui/button"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import { jewlr_t } from "helpers/application"

import ArticleCard from "./article-card"
import ArticleCardLoading from "./article-card-loading"
import Breadcrumb from "./breadcrumb"

const metaDescription = `Jewlr ${jewlr_t("terms.specialize")}s in high-quality ${jewlr_t("terms.jewelry")} — rings, earrings, necklaces, and more — ${jewlr_t("terms.personalized")} with your name or message for a gift or for yourself.`

const Homepage = ({ articleData }) => {
  const { search } = useLocation()
  const queryParams = search ? queryString.parse(search) : {}

  const queryFn = useCallback(
    ({ pageParam = 1 }) => api.get(`/blog.json?page=${pageParam}`),
    []
  )

  const ssrData = typeof window === "undefined" ? articleData : undefined

  const {
    data,
    isLoading,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteQuery({
    getNextPageParam: (lastPage) =>
      lastPage?.page?.current_page < lastPage?.page?.total_page_count
        ? lastPage?.page?.current_page + 1
        : undefined,
    getPreviousPageParam: (firstPage) =>
      firstPage?.page?.current_page > 1
        ? firstPage?.page?.current_page - 1
        : undefined,
    keepPreviousData: true,
    queryFn,
    queryKey: ["blogs"],
    select: (data) => {
      const { pageParams, pages } = data

      return {
        articles: (pages || []).reduce(
          (accu, curr) => [...accu, ...(curr?.articles || [])],
          []
        ),
        page: pages[pages.length - 1].page,
        pageParams,
      }
    },
    ...(ssrData?.articles && {
      initialData: {
        pageParams: ssrData?.page?.current_page
          ? [ssrData.page.current_page]
          : undefined,
        pages: [ssrData],
      },
      initialPageParam: ssrData?.page?.current_page,
    }),
  })

  const articles = data?.articles || []
  const featuredArticles = articles.filter((article) => article.featured)
  const blogArticles = articles.filter((article) => !article.featured)

  const placeholders = hasPreviousPage
    ? Math.min(
        data?.page?.current_page * data?.page?.results_per_page,
        data?.page?.total_article_count
      ) -
      data?.articles?.length -
      1
    : 0

  useEffect(() => {
    // When SSR loading a page > 1, we only have the data from the current page.
    // Here, we gradually fetch each previous page back to page 1.
    if (hasPreviousPage && !isFetchingPreviousPage) {
      fetchPreviousPage()
    }
  }, [fetchPreviousPage, isFetchingPreviousPage, hasPreviousPage])

  useEffect(() => {
    // Sync page query parameter
    if (
      data?.page?.current_page > 1 &&
      queryParams.page !== data?.page?.current_page?.toString()
    ) {
      const updatedParams = {
        ...queryParams,
        page: data?.page?.current_page,
      }
      history.replace({ search: queryString.stringify(updatedParams) })
    }
  }, [queryParams, data])

  return (
    <>
      <Helmet encodeSpecialCharacters={false}>
        {/* eslint-disable react/jsx-sort-props, react/no-unknown-property */}
        <title>
          Custom Name Necklace, Rings & Earrings {jewlr_t("terms.Jewelry")} Blog
        </title>
        <meta
          content={`Custom Name Necklace, Rings & Earrings ${jewlr_t("terms.Jewelry")} Blog`}
          property="og:title"
        />
        <meta content={metaDescription} name="description" />
        <meta content={metaDescription} property="og:description" />
        <meta
          content={`https://${sl.config.site_url}/blog`}
          property="og:url"
        />
        {featuredArticles.length > 0 && featuredArticles[0].cover_image && (
          <meta
            content={featuredArticles[0].cover_image.fallback}
            name="og_image"
            property="og:image"
          />
        )}
        {featuredArticles.length > 0 &&
          featuredArticles.map(
            (article) =>
              article.cover_image?.srcSet?.length > 0 &&
              article.cover_image.srcSet.map((srcset) => (
                <link
                  key={`article-homepage-featured-image-${article.url_path}-${srcset.media}`}
                  rel="preload"
                  fetchpriority="high"
                  as="image"
                  imagesrcset={srcset.srcSet}
                  imagesizes={srcset.sizes}
                  media={srcset.media}
                />
              ))
          )}
        {/* eslint-enable react/jsx-sort-props, react/no-unknown-property */}
      </Helmet>
      <Breadcrumb />
      <h1 className="text-32 leading-36 mb-40 font-bold">
        {sl.config.site_title} Blog &ndash; Custom Necklaces, Rings & Earrings
      </h1>
      <p className="text-16 leading-22 mb-16 font-light tracking-tight">
        Here you’ll find expert tips on choosing the perfect piece, deep dives
        into gemstones and materials, trend insights, and inspiration for every
        milestone worth celebrating. Whether you’re exploring custom rings, name
        necklaces, birthstone designs, or anniversary gifts, our articles help
        you understand what makes each piece special and how to choose{" "}
        {jewlr_t("terms.jewelry")} that tells your story.
      </p>
      <p className="text-16 leading-22 mb-16 font-light tracking-tight">
        From buying guides and care tips to {jewlr_t("terms.personalization")}{" "}
        ideas and behind‑the‑scenes craftsmanship, the Jewlr Blog is your
        trusted resource for learning, discovering, and celebrating life’s
        moments through {jewlr_t("terms.jewelry")}.
      </p>
      {isLoading || hasPreviousPage ? (
        <ArticleCardLoading featured />
      ) : (
        featuredArticles.map((featured) => (
          <ArticleCard article={featured} featured key={featured.url_path} />
        ))
      )}
      <div className="tablet:gap-16 tablet:grid-cols-3 desktop:gap-24 grid">
        {placeholders > 0 &&
          [...Array(placeholders)].map((_, i) => (
            <ArticleCardLoading key={`article-loading-${i}`} />
          ))}
        {blogArticles.map((blogArticle) => (
          <ArticleCard article={blogArticle} key={blogArticle.url_path} />
        ))}
        {isLoading &&
          [...Array(6)].map((_, i) => (
            <ArticleCardLoading key={`article-loading-${i}`} />
          ))}
      </div>
      {hasNextPage && (
        <div className="tablet:pb-40 flex justify-center px-20 pt-8">
          <Button
            aria-disabled={isFetchingNextPage}
            asChild
            className="w-260"
            loading={isFetchingNextPage}
            variant="secondary"
          >
            <Link
              onClick={(e) => {
                e.preventDefault()
                fetchNextPage()
              }}
              replace
              to={`/blog?page=${data?.page?.current_page + 1}`}
            >
              {isFetchingNextPage ? (
                <>
                  Loading
                  <FontAwesomeIcon className="ml-4" icon={faSpinner} spin />
                </>
              ) : (
                "View More"
              )}
            </Link>
          </Button>
        </div>
      )}
    </>
  )
}

Homepage.propTypes = {
  articleData: PropTypes.object,
}

export default Homepage
